import React, { useState } from 'react';
import { FadeIn } from './ui/FadeIn';
import { CtaButton } from './ui/CtaButton';
import { X, Gift, CheckCircle2, ChevronDown, FileText, Zap, Mail, Search, Lock } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
function LegalModal({
  isOpen,
  onClose,
  title,
  children
}: {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}) {
  return <AnimatePresence data-id="element-271">
      {isOpen && <motion.div initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2
    }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={onClose} data-id="element-272">
          <motion.div initial={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={{
        opacity: 0,
        scale: 0.95,
        y: 20
      }} transition={{
        duration: 0.25
      }} className="bg-brand-card border border-brand-gold/20 rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-id="element-273">
            <div className="flex items-center justify-between p-6 border-b border-brand-bg" data-id="element-274">
              <h3 className="text-xl font-bold text-white" data-id="element-275">{title}</h3>
              <button onClick={onClose} className="text-brand-muted hover:text-white transition-colors p-1" data-id="element-276">
                <X className="w-5 h-5" data-id="element-277" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto text-brand-muted text-sm leading-relaxed space-y-4" data-id="element-278">
              {children}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}
export function FinalCtaSection() {
  const [activeModal, setActiveModal] = useState<'privacy' | 'terms' | null>(null);
  const [showBonusDetails, setShowBonusDetails] = useState(false);
  const [bump1Selected, setBump1Selected] = useState(false);
  const [bump2Selected, setBump2Selected] = useState(false);
  const [showUpsellPopup, setShowUpsellPopup] = useState(false);
  const bonuses = [{
    id: 1,
    title: 'BONO #1 — KIT "CERO FRICCIÓN"',
    subtitle: 'Configura tus 4 cuentas en 30 minutos',
    image: 'https://cdn.magicpatterns.com/uploads/4syHH1GikufoyyhnLvaSb6/Bono_1.png',
    description: 'La mayoría compra el manual… y se atasca en el setup. No porque sea difícil. Sino porque no saben por dónde empezar. Este kit te quita esa fricción.',
    benefit: 'Te da nombres exactos, reglas copy/paste y un checklist paso a paso para que en 30 minutos dejes tus cuentas separadas, con funciones claras y listas para trabajar.',
    includes: ['Guía "Setup 30-min" (PDF ejecutable)', '3 rutas según tu situación (2, 3 o 4 cuentas)', 'Nombres recomendados (copy/paste directo a tu banco)', 'Plantilla de reglas rápidas (entrada/salida/movimiento)', 'Los 7 errores típicos del setup (y cómo evitarlos)', 'Checklist final imprimible'],
    forWho: 'Si te da flojera "pensar mucho" y solo quieres dejarlo listo hoy, este bono te ahorra horas de parálisis.'
  }, {
    id: 2,
    title: 'BONO #2 — GUÍA "ANTI-MEZCLA"',
    subtitle: 'Reglas de transferencias y pagos automáticos',
    image: 'https://cdn.magicpatterns.com/uploads/9ScnV6eAsvuUmvFq6MMLUS/bono_2.png',
    description: 'El sistema funciona cuando tú estás motivado. Pero se rompe cuando estás cansado, ocupado o ansioso. Por eso este bono automatiza el orden.',
    benefit: 'Te enseña qué transferir, cuándo, con qué frecuencia y bajo qué reglas para que tu dinero se separe sin que tengas que recordarlo.',
    includes: ['Guía "Automatizaciones Anti-Mezcla" (PDF)', '3 modelos de frecuencia (según tu tipo de ingreso)', '12 reglas "si-entonces" listas para copiar', 'Mapa de pagos automáticos (qué cuenta paga qué)', 'Plantilla de calendario mensual/quincenal', 'Checklist de configuración (1 tarde, listo)'],
    forWho: 'Si eres indisciplinado, si tus ingresos son variables, o si simplemente odias "estar encima" de tu dinero todo el tiempo.'
  }, {
    id: 3,
    title: 'BONO #3 — WORKBOOK "ANSIEDAD → ACCIÓN"',
    subtitle: '10 minutos para volver al control',
    image: 'https://cdn.magicpatterns.com/uploads/sYTRNwTF1ofk4DcEngUhDX/bono_3.png',
    description: 'Aquí está el punto donde la gente pierde dinero: Cuando sienten urgencia, mueven plata para calmarse. Y después se arrepienten.',
    benefit: 'Este workbook te da un protocolo de 10 minutos para frenar el impulso, bajar la activación y tomar la decisión correcta (no la que te calma 5 minutos).',
    includes: ['Workbook rellenable "Ansiedad → Acción" (PDF)', 'Reset de 90 segundos (respiración + postura + enfoque)', 'Plantilla "De emoción a número" (para ver qué te asusta realmente)', 'Decisión en 3 preguntas (función / tiempo / movimiento mínimo)', 'Lista de acciones permitidas (y prohibidas) bajo ansiedad', 'Tracker de impulsos 7 días (para descubrir tu patrón)', 'Tarjeta móvil "Pausa antes de mover" (versión de bolsillo)'],
    forWho: 'Si las noticias te activan, si tiendes a "invertir para recuperar", o si compras para calmarte… esto te salva.'
  }];
  const getCheckoutUrl = (withBumps = false, forceBundle = false) => {
    const currentParams = new URLSearchParams(window.location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    const checkoutParams = new URLSearchParams();
    utmKeys.forEach(key => {
      const value = currentParams.get(key);
      if (value) {
        checkoutParams.set(key, value);
      }
    });
    // Different Hotmart permalinks per bump combination
    let base = 'https://pay.hotmart.com/F104344877Y'; // Manual only (no bumps)
    if (forceBundle) {
      base = 'https://pay.hotmart.com/R104845730I'; // Manual + Bump 1 + Bump 2
    } else if (withBumps) {
      if (bump1Selected && bump2Selected) {
        base = 'https://pay.hotmart.com/R104845730I'; // Manual + Bump 1 + Bump 2
      } else if (bump1Selected) {
        base = 'https://pay.hotmart.com/F104841822E'; // Manual + Bump 1
      } else if (bump2Selected) {
        base = 'https://pay.hotmart.com/S104843527J'; // Manual + Bump 2
      }
    }
    const queryString = checkoutParams.toString();
    if (queryString) {
      return base.includes('?') ? `${base}&${queryString}` : `${base}?${queryString}`;
    }
    return base;
  };
  return <section className="w-full px-4 md:px-6 pb-8" data-id="element-279">
      <div className="max-w-3xl mx-auto" data-id="element-280">
        <FadeIn data-id="element-281">
          <div className="bg-gradient-to-b from-brand-card to-brand-bg border border-brand-gold rounded-2xl p-8 md:p-16 text-center shadow-2xl shadow-brand-gold/5 relative overflow-hidden" id="final-cta" data-id="element-282">
            {/* Background decorative glow */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-32 bg-brand-gold/10 blur-[100px] pointer-events-none" data-id="element-283"></div>

            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif tracking-tight" data-id="element-284">
              MANUAL DEL DINERO
            </h2>

            <div className="flex flex-col items-center mb-8" data-id="element-285">
              <div className="flex items-center gap-3 mb-2" data-id="element-286">
                <span className="text-xl md:text-3xl text-brand-muted line-through" data-id="element-287">
                  US$97
                </span>
                <span className="bg-brand-gold text-brand-bg text-xs md:text-sm font-bold px-2.5 py-1 md:px-3 md:py-1.5 rounded-md" data-id="element-288">
                  93% OFF
                </span>
              </div>
              <motion.span key={`price-${bump1Selected}-${bump2Selected}`} initial={{
              scale: 1.15,
              opacity: 0
            }} animate={{
              scale: 1,
              opacity: 1
            }} transition={{
              duration: 0.3,
              ease: 'easeOut'
            }} className="text-4xl md:text-6xl font-bold text-brand-gold inline-block" data-id="element-289">
                US$
                {(6.39 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0)).toFixed(2)}
              </motion.span>
            </div>

            <div className="space-y-2 text-brand-muted mb-10 text-lg" data-id="element-290">
              <p data-id="element-291">Acceso inmediato</p>
              <p data-id="element-292">
                Formato: ebook + plantillas + checklists + plan accionable +
                Bonos GRATIS
              </p>
            </div>

            {/* Bonuses summary */}
            <div className="bg-brand-bg/60 border border-brand-gold/15 rounded-xl p-6 mb-10 text-left max-w-md mx-auto" data-id="element-293">
              <div className="flex items-center gap-2 mb-4 justify-center" data-id="element-294">
                <Gift className="w-4 h-4 text-brand-gold" data-id="element-295" />
                <span className="text-sm font-bold text-brand-gold uppercase tracking-wide" data-id="element-296">
                  Bonos incluidos gratis (Valor $47 USD)
                </span>
              </div>
              <ul className="space-y-3" data-id="element-297">
                <li className="flex items-start gap-2.5 text-sm text-white" data-id="element-298">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" data-id="element-299" />
                  <span data-id="element-300">
                    Kit "Cero Fricción"{' '}
                    <span className="text-brand-muted" data-id="element-301">— Setup en 30 min</span>
                  </span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-white" data-id="element-302">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" data-id="element-303" />
                  <span data-id="element-304">
                    Guía "Anti-Mezcla"{' '}
                    <span className="text-brand-muted" data-id="element-305">
                      — Pagos automáticos
                    </span>
                  </span>
                </li>
                <li className="flex items-start gap-2.5 text-sm text-white" data-id="element-306">
                  <CheckCircle2 className="w-4 h-4 text-brand-gold mt-0.5 shrink-0" data-id="element-307" />
                  <span data-id="element-308">
                    Workbook "Ansiedad → Acción"{' '}
                    <span className="text-brand-muted" data-id="element-309">
                      — Control en 10 min
                    </span>
                  </span>
                </li>
              </ul>

              {/* Leer más toggle */}
              <button onClick={() => setShowBonusDetails(!showBonusDetails)} className="mt-4 w-full flex items-center justify-center gap-1.5 text-brand-gold text-sm font-medium hover:text-brand-gold-dark transition-colors py-2" data-id="element-310">
                <span data-id="element-311">{showBonusDetails ? 'Leer menos' : 'Leer más'}</span>
                <motion.div animate={{
                rotate: showBonusDetails ? 180 : 0
              }} transition={{
                duration: 0.3
              }} data-id="element-312">
                  <ChevronDown className="w-4 h-4" data-id="element-313" />
                </motion.div>
              </button>

              {/* Expandable bonus details */}
              <AnimatePresence data-id="element-314">
                {showBonusDetails && <motion.div initial={{
                height: 0,
                opacity: 0
              }} animate={{
                height: 'auto',
                opacity: 1
              }} exit={{
                height: 0,
                opacity: 0
              }} transition={{
                duration: 0.4,
                ease: 'easeInOut'
              }} className="overflow-hidden" data-id="element-315">
                    <div className="pt-4 space-y-6 border-t border-brand-gold/10 mt-2" data-id="element-316">
                      {bonuses.map((bono, index) => <div key={bono.id} className="bg-brand-bg/40 border border-brand-gold/10 rounded-xl overflow-hidden" data-id="element-317">
                          {/* Bonus image */}
                          <div className="relative h-48 overflow-hidden bg-brand-bg/50 flex items-center justify-center" data-id="element-318">
                            <img src={bono.image} alt={bono.title} className={`w-full h-full ${index !== 0 ? 'object-cover object-top' : 'object-cover'}`} data-id="element-319" />
                            <div className="absolute top-3 left-3 bg-brand-gold text-brand-bg font-bold text-xs px-2.5 py-1 rounded shadow-lg" data-id="element-320">
                              HOY: GRATIS
                            </div>
                          </div>

                          {/* Bonus content */}
                          <div className="p-5 space-y-4" data-id="element-321">
                            <div data-id="element-322">
                              <h4 className="text-base font-bold text-white mb-0.5" data-id="element-323">
                                {bono.title}
                              </h4>
                              <p className="text-brand-gold text-sm font-medium" data-id="element-324">
                                {bono.subtitle}
                              </p>
                            </div>

                            <p className="text-brand-text/90 text-sm leading-relaxed" data-id="element-325">
                              {bono.description}
                            </p>
                            <p className="text-brand-text/90 text-sm leading-relaxed font-medium" data-id="element-326">
                              {bono.benefit}
                            </p>

                            <div className="bg-brand-bg/50 rounded-lg p-4 border border-brand-gold/10" data-id="element-327">
                              <h5 className="text-white font-bold text-xs uppercase tracking-wide mb-2 flex items-center gap-1.5" data-id="element-328">
                                <FileText className="w-3.5 h-3.5 text-brand-gold" data-id="element-329" />
                                Lo que incluye:
                              </h5>
                              <ul className="space-y-1.5" data-id="element-330">
                                {bono.includes.map((item, i) => <li key={i} className="flex items-start gap-2 text-xs text-brand-muted" data-id="element-331">
                                    <CheckCircle2 className="w-3.5 h-3.5 text-brand-gold/70 mt-0.5 shrink-0" data-id="element-332" />
                                    <span data-id="element-333">{item}</span>
                                  </li>)}
                              </ul>
                            </div>

                            <div className="flex items-start gap-2 text-xs italic text-brand-muted bg-brand-gold/5 p-3 rounded-lg border-l-2 border-brand-gold" data-id="element-334">
                              <Zap className="w-3.5 h-3.5 text-brand-gold mt-0.5 shrink-0" data-id="element-335" />
                              <p data-id="element-336">
                                <strong className="text-brand-gold not-italic" data-id="element-337">
                                  Para quién:
                                </strong>{' '}
                                {bono.forWho}
                              </p>
                            </div>
                          </div>
                        </div>)}
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </div>

            <div className="max-w-md mx-auto mb-8" data-id="element-338">
              <CtaButton fullWidth onClick={() => {
              if (!bump1Selected && !bump2Selected) {
                setShowUpsellPopup(true);
                return;
              }
              if (window.fbq) {
                window.fbq('track', 'AddToCart', {
                  content_name: bump1Selected || bump2Selected ? 'Manual del Dinero + Extras' : 'Manual del Dinero',
                  content_type: 'product',
                  value: 6.39 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0),
                  currency: 'USD'
                });
              }
              window.open(getCheckoutUrl(true), '_blank');
            }} suffix={<span className="font-bold" data-id="element-340">
                    US$
                    {(6.39 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0)).toFixed(2)}
                  </span>} data-id="element-339">
                Completar Compra
              </CtaButton>
            </div>

            {/* Order Bumps */}
            <div className="max-w-md mx-auto mb-10 space-y-5" data-id="element-341">
              {/* ORDER BUMP #1 */}
              <div className={`relative rounded-xl overflow-hidden transition-all duration-300 bump-shimmer ${bump1Selected ? 'border-2 border-brand-gold shadow-lg shadow-brand-gold/10' : 'border-2 border-brand-gold/30'}`} data-id="element-342">
                {/* Top Banner */}
                <div className="bg-brand-gold text-brand-bg text-center py-1.5 px-4" data-id="element-343">
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-344">
                    ⭐ Altamente Recomendado ⭐
                  </span>
                </div>

                {/* Content */}
                <div className="bg-brand-bg/80 p-4" data-id="element-345">
                  <div className="flex items-start gap-3" data-id="element-346">
                    {/* Checkbox */}
                    <button onClick={() => setBump1Selected(!bump1Selected)} className="mt-3 shrink-0 w-5 h-5 rounded border-2 border-brand-gold/40 flex items-center justify-center transition-all duration-200 hover:border-brand-gold" style={{
                    backgroundColor: bump1Selected ? '#C9A84C' : 'transparent'
                  }} data-id="element-347">
                      {bump1Selected && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-348">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#0C0C0C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-349" />
                        </svg>}
                    </button>

                    {/* Thumbnail */}
                    <div className="shrink-0 w-20 h-24 rounded-lg overflow-hidden border border-brand-gold/10" data-id="element-350">
                      <img src="https://cdn.magicpatterns.com/uploads/8KGtPGTXBorbuLXzj8GwSv/ORDER_BUMP_1_Portada_El_Ritual_de_los_3_Sobres.png" alt="El Ritual de los 3 Sobres" className="w-full h-full object-cover" data-id="element-351" />
                    </div>

                    {/* Title + Price */}
                    <div className="flex-1 min-w-0 pt-1" data-id="element-352">
                      <h4 className="text-white font-bold text-sm leading-snug" data-id="element-353">
                        El Ritual de los 3 Sobres
                      </h4>
                      <p className="text-brand-gold font-semibold text-sm mt-1" data-id="element-354">
                        + $9.00
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-brand-muted text-xs leading-relaxed mt-3" data-id="element-355">
                    Agrega el Ritual de los 3 Sobres y recupera la conexión
                    física con tu dinero. Esta guía táctica te enseña a utilizar
                    tres contenedores para tus flujos más sensibles,
                    devolviéndote el control absoluto sobre cada billete que
                    sale de tu mano.
                  </p>
                </div>
              </div>

              {/* ORDER BUMP #2 */}
              <div className={`relative rounded-xl overflow-hidden transition-all duration-300 bump-shimmer bump-shimmer-delay ${bump2Selected ? 'border-2 border-brand-gold shadow-lg shadow-brand-gold/10' : 'border-2 border-brand-gold/30'}`} data-id="element-356">
                {/* Top Banner */}
                <div className="bg-brand-gold text-brand-bg text-center py-1.5 px-4" data-id="element-357">
                  <span className="text-xs font-bold uppercase tracking-wider" data-id="element-358">
                    ⭐ Altamente Recomendado ⭐
                  </span>
                </div>

                {/* Content */}
                <div className="bg-brand-bg/80 p-4" data-id="element-359">
                  <div className="flex items-start gap-3" data-id="element-360">
                    {/* Checkbox */}
                    <button onClick={() => setBump2Selected(!bump2Selected)} className="mt-3 shrink-0 w-5 h-5 rounded border-2 border-brand-gold/40 flex items-center justify-center transition-all duration-200 hover:border-brand-gold" style={{
                    backgroundColor: bump2Selected ? '#C9A84C' : 'transparent'
                  }} data-id="element-361">
                      {bump2Selected && <svg width="12" height="12" viewBox="0 0 12 12" fill="none" data-id="element-362">
                          <path d="M2.5 6L5 8.5L9.5 3.5" stroke="#0C0C0C" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-363" />
                        </svg>}
                    </button>

                    {/* Thumbnail */}
                    <div className="shrink-0 w-20 h-24 rounded-lg overflow-hidden border border-brand-gold/10" data-id="element-364">
                      <img src="https://cdn.magicpatterns.com/uploads/63YovtKwzidybLWsfdo5NR/ORDER_BUMP_2_Portada_Auditoria_Fugas_Invisibles.png" alt="Auditoría de Fugas Invisibles" className="w-full h-full object-cover" data-id="element-365" />
                    </div>

                    {/* Title + Price */}
                    <div className="flex-1 min-w-0 pt-1" data-id="element-366">
                      <h4 className="text-white font-bold text-sm leading-snug" data-id="element-367">
                        Auditoría de Fugas Invisibles
                      </h4>
                      <p className="text-brand-gold font-semibold text-sm mt-1" data-id="element-368">
                        + $9.00
                      </p>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-brand-muted text-xs leading-relaxed mt-3" data-id="element-369">
                    Agrega la Auditoría de Fugas Invisibles y sella las grietas
                    de tu sistema. Este diagnóstico de 15 minutos identifica y
                    elimina los drenajes silenciosos que comprometen tu
                    estabilidad, recuperando entre $50 y $100 USD mensuales de
                    capital que hoy simplemente desaparece.
                  </p>
                </div>
              </div>

              {/* Order Summary */}
              <div className="bg-brand-bg/60 border border-brand-gold/15 rounded-xl p-5 mt-6" data-id="element-370">
                <div className="space-y-2.5" data-id="element-371">
                  {/* Base items */}
                  <div className="flex items-center justify-between text-sm" data-id="element-372">
                    <span className="text-white" data-id="element-373">Manual del Dinero</span>
                    <span className="text-brand-muted" data-id="element-374">$97.00</span>
                  </div>
                  <div className="flex items-center justify-between text-sm" data-id="element-375">
                    <span className="text-white" data-id="element-376">3 Bonos Gratis</span>
                    <span className="text-brand-muted" data-id="element-377">$47.00</span>
                  </div>

                  {/* Conditional bump items */}
                  {bump1Selected && <div className="flex items-center justify-between text-sm" data-id="element-378">
                      <span className="text-brand-gold" data-id="element-379">
                        El Ritual de los 3 Sobres
                      </span>
                      <span className="text-brand-gold" data-id="element-380">$9.00</span>
                    </div>}
                  {bump2Selected && <div className="flex items-center justify-between text-sm" data-id="element-381">
                      <span className="text-brand-gold" data-id="element-382">
                        Auditoría de Fugas Invisibles
                      </span>
                      <span className="text-brand-gold" data-id="element-383">$9.00</span>
                    </div>}

                  {/* Divider */}
                  <div className="border-t border-brand-gold/10 my-1" data-id="element-384"></div>

                  {/* Valor Total */}
                  <div className="flex items-center justify-between text-sm" data-id="element-385">
                    <span className="text-white font-bold" data-id="element-386">Valor Total</span>
                    <span className="text-brand-muted line-through" data-id="element-387">
                      $
                      {(144 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0)).toFixed(2)}
                    </span>
                  </div>

                  {/* Hoy pagas solo */}
                  <div className="flex items-center justify-between" data-id="element-388">
                    <span className="text-brand-gold font-bold text-base" data-id="element-389">
                      Hoy pagas solo
                    </span>
                    <span className="text-brand-gold font-bold text-xl" data-id="element-390">
                      $
                      {(6.39 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0)).toFixed(2)}
                    </span>
                  </div>

                  {/* Dynamic CTA */}
                  <div className="pt-4" data-id="element-391">
                    <CtaButton fullWidth onClick={() => {
                    if (!bump1Selected && !bump2Selected) {
                      setShowUpsellPopup(true);
                      return;
                    }
                    if (window.fbq) {
                      window.fbq('track', 'AddToCart', {
                        content_name: bump1Selected || bump2Selected ? 'Manual del Dinero + Extras' : 'Manual del Dinero',
                        content_type: 'product',
                        value: 6.39 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0),
                        currency: 'USD'
                      });
                    }
                    window.open(getCheckoutUrl(true), '_blank');
                  }} suffix={<span className="font-bold" data-id="element-393">
                          US$
                          {(6.39 + (bump1Selected ? 9 : 0) + (bump2Selected ? 9 : 0)).toFixed(2)}
                        </span>} data-id="element-392">
                      Completar Compra
                    </CtaButton>
                  </div>
                </div>
              </div>
            </div>

            <p className="text-brand-text/80 italic text-sm md:text-base" data-id="element-394">
              No "cuando tengas tiempo". <br data-id="element-395" />
              Porque el desorden siempre cobra primero.
            </p>
          </div>

          <div className="mt-12 text-center text-brand-muted text-xs space-y-3" data-id="element-396">
            <div className="flex items-center justify-center gap-3" data-id="element-397">
              <button onClick={() => setActiveModal('privacy')} className="underline underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-398">
                Política de Privacidad
              </button>
              <span className="text-brand-muted/40" data-id="element-399">·</span>
              <button onClick={() => setActiveModal('terms')} className="underline underline-offset-2 hover:text-brand-gold transition-colors" data-id="element-400">
                Términos y Condiciones
              </button>
            </div>
            <p data-id="element-401">
              © {new Date().getFullYear()} Manual del Dinero. Todos los
              derechos reservados.
            </p>
          </div>
        </FadeIn>
      </div>

      {/* Exit Intent Upsell Popup */}
      <AnimatePresence data-id="element-402">
        {showUpsellPopup && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm" onClick={() => setShowUpsellPopup(false)} data-id="element-403">
            <motion.div initial={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.95,
          y: 20
        }} transition={{
          duration: 0.25
        }} className="bg-brand-card border border-brand-gold/20 rounded-2xl w-full max-w-lg flex flex-col shadow-2xl shadow-black/50" onClick={e => e.stopPropagation()} data-id="element-404">
              {/* Header Banner */}
              <div className="bg-brand-gold/10 border-b border-brand-gold/20 px-4 py-3 text-center relative" data-id="element-405">
                <button onClick={() => setShowUpsellPopup(false)} className="absolute top-3 right-3 text-brand-muted hover:text-white transition-colors" data-id="element-406">
                  <X className="w-5 h-5" data-id="element-407" />
                </button>
                <div className="inline-block bg-red-500/20 text-red-400 text-[10px] md:text-xs font-bold px-2.5 py-0.5 rounded-full mb-2 flash-badge border border-red-500/30" data-id="element-408">
                  ⚡ OFERTA ÚNICA — SOLO EN ESTA PANTALLA ⚡
                </div>
                <h3 className="text-lg md:text-2xl font-bold text-white mb-1 leading-tight" data-id="element-409">
                  🛑 ¡Espera! No te vayas sin esto...
                </h3>
                <p className="text-brand-gold font-medium text-xs md:text-sm" data-id="element-410">
                  Descubrimos algo que puede cambiar tu resultado
                </p>
              </div>

              {/* Body */}
              <div className="px-4 py-3 md:p-8 space-y-3 md:space-y-6" data-id="element-411">
                <div className="text-center space-y-2 md:space-y-4" data-id="element-412">
                  <div className="inline-block bg-brand-gold text-brand-bg font-black text-base md:text-xl px-3 py-1 md:px-4 md:py-1.5 rounded-lg transform -rotate-2 flash-badge shadow-lg shadow-brand-gold/20" data-id="element-413">
                    30% OFF - PAQUETE COMPLETO
                  </div>
                  <p className="text-brand-text/90 text-xs md:text-base leading-relaxed" data-id="element-414">
                    El Manual del Dinero te da el sistema. Pero el Ritual de los
                    3 Sobres y la Auditoría de Fugas Invisibles te dan el
                    control total — recuperando hasta $100 USD mensuales que hoy
                    se te escapan.
                  </p>
                </div>

                {/* What's Included */}
                <div className="bg-brand-bg/50 border border-brand-gold/10 rounded-xl p-3 md:p-5" data-id="element-415">
                  <ul className="space-y-1.5 md:space-y-3" data-id="element-416">
                    <li className="flex items-start gap-2 text-xs md:text-sm text-white" data-id="element-417">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-418" />
                      <span data-id="element-419">
                        Manual del Dinero (ebook + plantillas + plan accionable)
                      </span>
                    </li>
                    <li className="flex items-start gap-2 text-xs md:text-sm text-white" data-id="element-420">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-421" />
                      <span data-id="element-422">
                        3 Bonos GRATIS (Kit Cero Fricción + Guía Anti-Mezcla +
                        Workbook Ansiedad→Acción)
                      </span>
                    </li>
                    <li className="flex items-start gap-2 text-xs md:text-sm text-brand-gold" data-id="element-423">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-424" />
                      <span data-id="element-425">
                        El Ritual de los 3 Sobres — Recupera la conexión física
                        con tu dinero
                      </span>
                    </li>
                    <li className="flex items-start gap-2 text-xs md:text-sm text-brand-gold" data-id="element-426">
                      <CheckCircle2 className="w-4 h-4 md:w-5 md:h-5 text-brand-gold shrink-0 mt-0.5" data-id="element-427" />
                      <span data-id="element-428">
                        Auditoría de Fugas Invisibles — Identifica y elimina
                        drenajes silenciosos
                      </span>
                    </li>
                  </ul>
                </div>

                {/* Pricing Block */}
                <div className="text-center space-y-1 md:space-y-2" data-id="element-429">
                  <div className="flex items-center justify-center gap-3" data-id="element-430">
                    <span className="text-brand-muted line-through text-sm md:text-lg" data-id="element-431">
                      US$24.39
                    </span>
                    <span className="text-3xl md:text-4xl font-bold text-brand-gold" data-id="element-432">
                      US$18.99
                    </span>
                  </div>
                  <p className="text-[10px] md:text-xs text-brand-muted uppercase tracking-wider font-semibold" data-id="element-433">
                    Precio especial solo disponible en esta pantalla
                  </p>
                  <div className="flex items-center justify-center gap-1.5 text-[10px] md:text-xs text-green-400/80" data-id="element-434">
                    <Lock className="w-3 h-3 md:w-3.5 md:h-3.5" data-id="element-435" />
                    <span data-id="element-436">Pago 100% seguro</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="space-y-2 md:space-y-4 pt-1 md:pt-4" data-id="element-437">
                  <motion.button whileHover={{
                scale: 1.02
              }} whileTap={{
                scale: 0.98
              }} initial={{
                boxShadow: '0 0 0 rgba(201, 168, 76, 0)'
              }} animate={{
                boxShadow: ['0 0 0 rgba(201, 168, 76, 0)', '0 0 15px rgba(201, 168, 76, 0.3)', '0 0 0 rgba(201, 168, 76, 0)']
              }} transition={{
                boxShadow: {
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut'
                },
                scale: {
                  duration: 0.1
                }
              }} onClick={() => {
                if (window.fbq) {
                  window.fbq('track', 'AddToCart', {
                    content_name: 'Manual del Dinero + Extras',
                    content_type: 'product',
                    value: 18.99,
                    currency: 'USD'
                  });
                }
                window.open('https://pay.hotmart.com/R104845730I?off=553w9hmi', '_blank');
                setShowUpsellPopup(false);
              }} className="w-full bg-brand-gold hover:bg-brand-gold-dark text-brand-bg font-bold py-3 px-4 md:py-4 md:px-6 rounded-lg text-sm md:text-lg transition-colors duration-200 shadow-lg shadow-brand-gold/10" data-id="element-438">
                    ¡SÍ! Quiero el Paquete Completo por US$18.99
                  </motion.button>

                  <button onClick={() => {
                if (window.fbq) {
                  window.fbq('track', 'AddToCart', {
                    content_name: 'Manual del Dinero',
                    content_type: 'product',
                    value: 6.39,
                    currency: 'USD'
                  });
                }
                window.open(getCheckoutUrl(false), '_blank');
                setShowUpsellPopup(false);
              }} className="w-full text-center text-xs md:text-sm text-brand-muted hover:text-white transition-colors underline underline-offset-4 py-1 md:py-2" data-id="element-439">
                    No gracias, continuar sin los extras →
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>

      {/* Privacy Policy Modal */}
      <LegalModal isOpen={activeModal === 'privacy'} onClose={() => setActiveModal(null)} title="Política de Privacidad" data-id="element-440">
        <p data-id="element-441">
          <strong className="text-white" data-id="element-442">Última actualización:</strong> Febrero
          2026
        </p>
        <p data-id="element-443">
          En Manual del Dinero, respetamos tu privacidad y nos comprometemos a
          proteger tu información personal.
        </p>
        <p data-id="element-444">
          <strong className="text-white" data-id="element-445">1. Información que recopilamos</strong>
        </p>
        <p data-id="element-446">
          Recopilamos únicamente la información necesaria para procesar tu
          compra: nombre, correo electrónico y datos de pago. No almacenamos
          información de tarjetas de crédito directamente; esta es procesada por
          nuestro proveedor de pagos seguro.
        </p>
        <p data-id="element-447">
          <strong className="text-white" data-id="element-448">2. Uso de la información</strong>
        </p>
        <p data-id="element-449">
          Tu información se utiliza exclusivamente para: entregar el producto
          digital adquirido, enviar actualizaciones relacionadas con tu compra,
          y mejorar nuestros servicios.
        </p>
        <p data-id="element-450">
          <strong className="text-white" data-id="element-451">3. Compartir información</strong>
        </p>
        <p data-id="element-452">
          No vendemos, alquilamos ni compartimos tu información personal con
          terceros, excepto cuando sea necesario para procesar tu compra o
          cuando la ley lo requiera.
        </p>
        <p data-id="element-453">
          <strong className="text-white" data-id="element-454">4. Seguridad</strong>
        </p>
        <p data-id="element-455">
          Implementamos medidas de seguridad técnicas y organizativas para
          proteger tu información contra acceso no autorizado, alteración o
          destrucción.
        </p>
        <p data-id="element-456">
          <strong className="text-white" data-id="element-457">5. Tus derechos</strong>
        </p>
        <p data-id="element-458">
          Puedes solicitar acceso, corrección o eliminación de tus datos
          personales en cualquier momento contactándonos directamente.
        </p>
        <p data-id="element-459">
          <strong className="text-white" data-id="element-460">6. Contacto</strong>
        </p>
        <p data-id="element-461">
          Para cualquier consulta sobre privacidad, puedes escribirnos y te
          responderemos en un plazo máximo de 48 horas.
        </p>
      </LegalModal>

      {/* Terms & Conditions Modal */}
      <LegalModal isOpen={activeModal === 'terms'} onClose={() => setActiveModal(null)} title="Términos y Condiciones" data-id="element-462">
        <p data-id="element-463">
          <strong className="text-white" data-id="element-464">Última actualización:</strong> Febrero
          2026
        </p>
        <p data-id="element-465">
          Al adquirir el Manual del Dinero, aceptas los siguientes términos y
          condiciones.
        </p>
        <p data-id="element-466">
          <strong className="text-white" data-id="element-467">1. Producto digital</strong>
        </p>
        <p data-id="element-468">
          El Manual del Dinero es un producto digital (ebook) que incluye
          plantillas, checklists y un plan accionable. Se entrega en formato
          digital inmediatamente después de la compra.
        </p>
        <p data-id="element-469">
          <strong className="text-white" data-id="element-470">2. Uso personal</strong>
        </p>
        <p data-id="element-471">
          El contenido adquirido es para uso personal y no puede ser
          redistribuido, revendido, compartido públicamente ni reproducido sin
          autorización expresa.
        </p>
        <p data-id="element-472">
          <strong className="text-white" data-id="element-473">3. Propiedad intelectual</strong>
        </p>
        <p data-id="element-474">
          Todo el contenido del Manual del Dinero, incluyendo textos,
          plantillas, gráficos y metodologías, está protegido por derechos de
          autor.
        </p>
        <p data-id="element-475">
          <strong className="text-white" data-id="element-476">4. Naturaleza educativa</strong>
        </p>
        <p data-id="element-477">
          Este producto tiene fines educativos e informativos. No constituye
          asesoría financiera profesional. Las decisiones financieras son
          responsabilidad exclusiva del comprador.
        </p>
        <p data-id="element-478">
          <strong className="text-white" data-id="element-479">5. Política de reembolso</strong>
        </p>
        <p data-id="element-480">
          Dado que se trata de un producto digital con entrega inmediata, no se
          ofrecen reembolsos una vez realizada la descarga. Si tienes problemas
          técnicos con la entrega, contáctanos y lo resolveremos.
        </p>
        <p data-id="element-481">
          <strong className="text-white" data-id="element-482">6. Modificaciones</strong>
        </p>
        <p data-id="element-483">
          Nos reservamos el derecho de modificar estos términos. Las
          modificaciones serán efectivas desde su publicación.
        </p>
      </LegalModal>
    </section>;
}